import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    body: `Crowned Canvas Creations LLC ("Crowned") respects your privacy. This policy explains, in plain language, what we collect and how we use it.

WHAT WE COLLECT
— Contact details you give us (name, email, and optionally phone number) through our forms, guide sign-ups, and checkout.
— Order and creative information you share for your commission, including photographs, photo links, and creative direction.
— Payment information is processed securely by Stripe. Crowned never sees or stores your full card number.
— Basic site analytics (pages visited, interactions) used to improve the experience.

HOW WE USE IT
— To create, proof, produce and deliver your custom artwork.
— To communicate with you about your order, questionnaire, proofs and delivery.
— To send the free guide you requested and occasional Crowned updates. You can unsubscribe at any time.
— SMS: if you provide a phone number, you are only enrolled in text messages when you explicitly opt in. Reply STOP to end texts at any time.

THIRD PARTIES
We use a small number of trusted processors: Stripe (payments), Payhip (guide delivery), Google Forms (intake and questionnaires), and email delivery providers. Each handles data under its own privacy policy.

YOUR RIGHTS
You may request a copy, correction, or deletion of your personal information at any time by contacting us through the Contact page.

This policy is provided by Crowned Canvas Creations LLC, Madison, Wisconsin, and may be updated as the business grows. Questions? Reach us through the Contact page.`,
  },
  terms: {
    title: "Terms & Conditions",
    body: `Welcome to Crowned Canvas Creations LLC. By commissioning an experience (Royal Essence, Sovereign Canvas, or Majestic Vibes), you agree to the following terms.

THE COMMISSION
Each Crowned experience is a custom, made-to-order creative commission. Pricing is shown as a total experience price, an initial payment due at booking, and a remaining balance. The initial payment begins your experience; it is not the full price. The remaining balance is invoiced after final approval of your artwork, before production.

YOUR CONTENT
You confirm you have the right to use any photographs or references you submit. You keep ownership of your source photographs. Crowned retains the right to display finished artwork in its portfolio unless you ask us in writing to keep your piece private.

CREATIVE PROCESS
Every experience includes a Creative Questionnaire, a first proof, and up to two complimentary revision rounds. Production begins only after your written final approval.

AVAILABILITY
Royal Essence photography is coordinated and confirmed with you after booking and is anchored to the Madison, Wisconsin area and surrounding region by arrangement. Sovereign Canvas and Majestic Vibes are available online to customers anywhere.

GENERAL
Timelines are estimates and may shift with revision rounds and approval timing. These terms may be updated; the version published here applies to new orders.`,
  },
  refund: {
    title: "Refund & Cancellation Policy",
    body: `Because every Crowned piece is custom-commissioned artwork, refunds depend on where your order is in the creative process.

BEFORE CREATIVE WORK BEGINS
If you cancel before completing your Creative Questionnaire and before any creative work has started, contact us and we will review a refund of your initial payment.

AFTER CREATIVE WORK HAS BEGUN
Once artwork development has started, the initial payment becomes non-refundable, as it covers creative time already invested in your commission.

AFTER FINAL APPROVAL
Once you approve your final artwork and the piece enters production, the order cannot be cancelled or refunded.

DAMAGED OR INCORRECT ORDERS
If your artwork arrives damaged or materially different from the approved proof, contact us within 7 days of delivery with photos and we will make it right — repair, replacement, or other resolution at Crowned's discretion.

HOW TO REQUEST
Contact us through the Contact page with your order number and we'll respond personally.`,
  },
  shipping: {
    title: "Shipping Policy",
    body: `PRODUCTION TIME
Production begins only after your final approval and remaining payment. Most Crowned pieces are produced and shipped within 7–14 business days of final approval; we'll share a more precise window with your proof.

DELIVERY
Finished artwork is packaged with care and shipped to the address you provide. Tracking is shared when your piece ships. Shipping for the standard 16×20 canvas experience is included in your experience price unless stated otherwise at checkout.

WHERE WE SHIP
Crowned is based in Madison, Wisconsin and ships within the United States. For anything outside the US, contact us before ordering.

DAMAGE IN TRANSIT
Inspect your piece on arrival. If it's damaged, photograph the packaging and artwork and contact us within 7 days so we can resolve it with the carrier and our studio.`,
  },
  revisions: {
    title: "Revision Policy",
    body: `Every Crowned commission includes a first proof and up to two complimentary revision rounds.

WHAT COUNTS AS A ROUND
A revision round is one consolidated set of feedback on a proof. We encourage you to gather all notes into a single response so each round counts fully.

BEYOND TWO ROUNDS
Additional revision rounds beyond the two included may be quoted at a per-round fee before the work continues.

FINAL APPROVAL
Production begins only after your written final approval. Changes requested after final approval may require a new production order.

SCOPE
Revisions refine the approved creative direction. A completely new concept or a new source photograph after artwork creation may be treated as a new commission.`,
  },
  accessibility: {
    title: "Accessibility",
    body: `Crowned Canvas Creations is committed to a web experience that works for everyone.

We aim to align with WCAG 2.1 AA guidelines — readable contrast, keyboard-friendly navigation, descriptive alt text, and clear structure.

If you experience any accessibility barrier on this site, please contact us through the Contact page and tell us what happened. We take these reports seriously and will address them directly.`,
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const doc = POLICIES[slug] || { title: "Not Found", body: "This policy could not be found." };
  return (
    <div className="relative">
      <SEO
        title={`${doc.title} | Crowned Canvas Creations`}
        description={`${doc.title} for Crowned Canvas Creations LLC, Madison, Wisconsin — custom artwork commissions.`}
        path={`/legal/${slug}`}
      />
      <Header />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="max-w-[820px] mx-auto px-6 md:px-12">
          <Link to="/" className="overline hover:text-white transition-colors">← Home</Link>
          <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mt-6">{doc.title}</h1>
          <p className="text-white/70 mt-8 leading-relaxed font-light text-base md:text-lg whitespace-pre-line">
            {doc.body}
          </p>
          <p className="mt-10 text-xs text-white/40 uppercase tracking-[0.22em]">
            Crowned Canvas Creations LLC · Madison, Wisconsin · Policies are reviewed and updated as the business grows.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
